"use client";

import { useState, useEffect, useRef } from "react";

const VIDEO_ID = "sOS5U7KksAk";

const slides = [
  {
    type: "image",
    img: "/images/banners/hero-peptides-home.jpg",
    srcBase: "/images/banners/hero-peptides-home",
    widths: [768, 1280, 1866, 2560],
    href: "/human-medicine/peptides/",
    alt: "Peptides now available: BPC-157, Semax and KPV from CareFirst Specialty Pharmacy",
    // Keep the left-aligned headline in frame when mobile crops the sides
    position: "15% center",
  },
  { type: "video" },
  { type: "image", img: "/images/banners/nimble-banner-3.jpeg" },
  { type: "image", img: "/images/hero-methylene.jpg" },
  { type: "image", img: "/images/hero-thyroid.jpg" },
  { type: "image", img: "/images/hero-hrt.jpg" },
];

const VIDEO_INDEX = slides.findIndex((s) => s.type === "video");
const FIRST_IMAGE_INDEX = slides.findIndex((s) => s.type === "image");

function SlideImage({ slide, index }) {
  const eager = index === FIRST_IMAGE_INDEX;
  const srcSet = (ext) => slide.widths.map((w) => `${slide.srcBase}-${w}w.${ext} ${w}w`).join(", ");
  const img = (
    <img
      src={slide.img}
      srcSet={slide.srcBase ? srcSet("jpg") : undefined}
      sizes={slide.srcBase ? "100vw" : undefined}
      alt={slide.alt || `Slide ${index + 1}`}
      width={1866}
      height={510}
      className="w-full h-full object-cover"
      style={slide.position ? { objectPosition: slide.position } : undefined}
      loading={eager ? "eager" : "lazy"}
      fetchPriority={eager ? "high" : undefined}
    />
  );
  if (!slide.srcBase) return img;
  return (
    <picture>
      <source type="image/webp" srcSet={srcSet("webp")} sizes="100vw" />
      {img}
    </picture>
  );
}

export default function HeroSlider() {

  const [current, setCurrent] = useState(0);
  const [posterVisible, setPosterVisible] = useState(true);
  const ytContainerRef = useRef(null);
  const playerRef = useRef(null);
  const loopIntervalRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    const duration = current === VIDEO_INDEX ? 30000 : 5000;
    const timer = setTimeout(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, duration);
    return () => clearTimeout(timer);
  }, [current]);

  // Restart video from the beginning every time its slide becomes active
  useEffect(() => {
    if (current === VIDEO_INDEX && playerRef.current?.seekTo) {
      playerRef.current.seekTo(0, true);
      playerRef.current.playVideo();
    }
  }, [current]);

  useEffect(() => {
    const initPlayer = () => {
      if (!ytContainerRef.current || playerRef.current) return;
      playerRef.current = new window.YT.Player(ytContainerRef.current, {
        host: "https://www.youtube.com",
        videoId: VIDEO_ID,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0,
        },
        events: {
          onReady: (e) => {
            e.target.mute();
            e.target.playVideo();
            e.target.getIframe().style.pointerEvents = "none";
            loopIntervalRef.current = setInterval(() => {
              try {
                const p = playerRef.current;
                if (!p || p.getPlayerState() !== 1) return;
                const duration = p.getDuration();
                if (duration > 0 && p.getCurrentTime() >= duration - 1) {
                  p.seekTo(0, true);
                }
              } catch (_) {}
            }, 200);
          },
          onStateChange: (e) => {
            // Hide poster only once the first frame is actually painted
            if (e.data === window.YT.PlayerState.PLAYING) {
              setPosterVisible(false);
            }
          },
        },
      });
    };

    const loadYT = () => {
      if (window.YT?.Player) {
        initPlayer();
      } else {
        window.onYouTubeIframeAPIReady = initPlayer;
        if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
          const tag = document.createElement("script");
          tag.src = "https://www.youtube.com/iframe_api";
          document.head.appendChild(tag);
        }
      }
    };

    if (document.readyState === "complete") {
      loadYT();
    } else {
      window.addEventListener("load", loadYT, { once: true });
    }

    return () => {
      clearInterval(loopIntervalRef.current);
      if (playerRef.current?.destroy) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, []);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchMove = (e) => { touchEndX.current = e.touches[0].clientX; };
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (diff > 50) setCurrent((prev) => (prev + 1) % slides.length);
    else if (diff < -50) setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative w-full flex-shrink-0">
      {/* Height driver — CSS class sets breakpoint-aware padding-bottom (1866×510 aspect ratio) */}
      <div className="hero-pb" />

      {/* Slider fills the driven height */}
      <section
        className="absolute inset-0 overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="absolute inset-0">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === current ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
              }`}
            >
              {slide.type === "video" ? (
                <div className="yt-hero-wrapper">
                  {/* maxresdefault poster — shown while the iframe initializes, then fades out */}
                  <img
                    src={`https://i.ytimg.com/vi/${VIDEO_ID}/maxresdefault.jpg`}
                    alt=""
                    aria-hidden="true"
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                      posterVisible ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                  />
                  <div ref={ytContainerRef} className="absolute inset-0 w-full h-full" />
                </div>
              ) : slide.href?.startsWith("/") ? (
                <a href={slide.href} className="block w-full h-full">
                  <SlideImage slide={slide} index={index} />
                </a>
              ) : slide.href ? (
                <a href={slide.href} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                  <SlideImage slide={slide} index={index} />
                </a>
              ) : (
                <SlideImage slide={slide} index={index} />
              )}
            </div>
          ))}
        </div>

        <button
          type="button"
          aria-label="Previous slide"
          className="hidden md:flex absolute z-20 left-4 top-1/2 -translate-y-1/2
             text-white bg-black bg-opacity-50 rounded-full p-2
             hover:bg-opacity-70 cursor-pointer
             focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
          onClick={() => setCurrent((prev) => (prev - 1 + slides.length) % slides.length)}
        >
          &#8592;
        </button>

        <button
          type="button"
          aria-label="Next slide"
          className="hidden md:flex absolute z-20 right-4 top-1/2 -translate-y-1/2
             text-white bg-black bg-opacity-50 rounded-full p-2
             hover:bg-opacity-70 cursor-pointer
             focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
          onClick={() => setCurrent((prev) => (prev + 1) % slides.length)}
        >
          &#8594;
        </button>
      </section>
    </div>
  );
}
